/**
 * =============================================================================
 * ParticleEditorScene - Editor visual de efectos de partículas
 * =============================================================================
 *
 * Pre-carga el spritesheet de partículas y un body+head de ejemplo.
 * Muestra preview en tiempo real sobre el personaje.
 */

import Phaser from 'phaser';
import { getAssetUrl } from '@shared/constants';
import {
  ParticleEmitter,
  createDefaultEffectDef,
  PARTICLE_SHEET,
  type ParticleEffectDef,
} from '../systems/ParticleSystem';
import { BODY_COLUMNS, BODY_ROWS } from '../sprites/BodySpriteManager';
import { addGridSpriteSheet } from '../sprites/gridSpriteSheet';

// ─── Constantes de posicionamiento (mismas que GameScene) ─────────────────────

const BODY_OFFSET_Y = -26;
const BODY_FRAME_HEIGHT = 64;
const HEAD_FRAME_HEIGHT = 30;
const HEAD_OFFSET_FROM_BODY_BOTTOM = 27;
const HEAD_COLUMNS = 4;

// ─── Callbacks estáticos ──────────────────────────────────────────────────────

export type SheetLoadedCallback = (cols: number, rows: number, totalFrames: number) => void;
export type DefChangedCallback = (def: ParticleEffectDef) => void;

/** Modos de anclaje del preview en el editor. */
export type EditorAnchorMode =
  | 'body_feet'
  | 'body_center'
  | 'body_head'
  | 'weapon_tip'
  | 'weapon_hilt'
  | 'shield_front'
  | 'helm_top'
  | 'tile'
  | 'item_ground'
  | 'projectile';

export interface EditorMetrics {
  activeParticles: number;
  fps: number;
}

export class ParticleEditorScene extends Phaser.Scene {
  public static onSheetLoaded: SheetLoadedCallback | null = null;
  public static onDefChanged: DefChangedCallback | null = null;

  // Estado
  private currentDef: ParticleEffectDef = createDefaultEffectDef();
  private emitter: ParticleEmitter | null = null;
  private bodySprite: Phaser.GameObjects.Sprite | null = null;
  private headSprite: Phaser.GameObjects.Sprite | null = null;
  private weaponSprite: Phaser.GameObjects.Sprite | null = null;
  private shieldSprite: Phaser.GameObjects.Sprite | null = null;
  private helmSprite: Phaser.GameObjects.Sprite | null = null;
  private tileSprite: Phaser.GameObjects.Rectangle | null = null;
  private itemGroundSprite: Phaser.GameObjects.Rectangle | null = null;
  private anchorMode: EditorAnchorMode = 'body_center';

  // Projectile mode state
  private projectileFrom: { x: number; y: number } | null = null;
  private projectileTo: { x: number; y: number } | null = null;
  private projectileStartedAt: number = 0;
  private projectileDurationMs: number = 1000;
  private projectileFromMarker: Phaser.GameObjects.Arc | null = null;
  private projectileToMarker: Phaser.GameObjects.Arc | null = null;
  private projectilePathLine: Phaser.GameObjects.Graphics | null = null;
  private projectileHintText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: 'ParticleEditorScene' });
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  preload(): void {
    // Spritesheet de partículas
    this.load.spritesheet(PARTICLE_SHEET.key, getAssetUrl(PARTICLE_SHEET.path), {
      frameWidth: PARTICLE_SHEET.frameWidth,
      frameHeight: PARTICLE_SHEET.frameHeight,
    });

    // Body y head de ejemplo (body1 = vestimenta básica, head1)
    this.load.image('editor_body1', getAssetUrl('entities/body/anims/body1.png'));
    this.load.spritesheet('editor_head1', getAssetUrl('entities/head/head1.png'), {
      frameWidth: 23,
      frameHeight: HEAD_FRAME_HEIGHT,
    });
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#111118');

    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    // Convertir body image a spritesheet
    this.setupBodySpritesheet();

    // Calcular posiciones como en GameScene
    const bodyBottomY = cy + BODY_OFFSET_Y + BODY_FRAME_HEIGHT / 2;
    const headCenterY = bodyBottomY - HEAD_OFFSET_FROM_BODY_BOTTOM - HEAD_FRAME_HEIGHT / 2;

    // Body sprite (idle, mirando abajo = frame 0)
    this.bodySprite = this.add.sprite(cx, bodyBottomY, 'editor_body1', 0);
    this.bodySprite.setOrigin(0.5, 1);
    this.bodySprite.setDepth(5000);

    // Head sprite (mirando abajo = frame 0)
    this.headSprite = this.add.sprite(cx, headCenterY, 'editor_head1', 0);
    this.headSprite.setOrigin(0.5, 0.5);
    this.headSprite.setDepth(5001);

    // Marcar sheet listo
    this.currentDef.spriteKey = PARTICLE_SHEET.key;
    this.currentDef.frames = [0];

    const total = PARTICLE_SHEET.cols * PARTICLE_SHEET.rows;
    ParticleEditorScene.onSheetLoaded?.(PARTICLE_SHEET.cols, PARTICLE_SHEET.rows, total);
  }

  update(_time: number, delta: number): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    // Mantener personaje centrado
    if (this.bodySprite) {
      const bodyBottomY = cy + BODY_OFFSET_Y + BODY_FRAME_HEIGHT / 2;
      const headCenterY = bodyBottomY - HEAD_OFFSET_FROM_BODY_BOTTOM - HEAD_FRAME_HEIGHT / 2;
      this.bodySprite.setPosition(cx, bodyBottomY);
      if (this.headSprite) {
        this.headSprite.setPosition(cx, headCenterY);
      }
    }

    // Emitter anclado según anchorMode
    if (this.emitter) {
      const { x, y } = this.resolveAnchorPosition(cx, cy);
      this.emitter.setPosition(x, y);
      this.emitter.update(delta);
    }
  }

  // ─── Projectile mode (anchor 'projectile') ─────────────────────────────────

  private installProjectileInput(): void {
    if (this.projectileHintText) return; // ya instalado
    const cx = this.scale.width / 2;
    this.projectileHintText = this.add
      .text(cx, 40, 'Modo projectile: click 1 = origen, click 2 = destino', {
        fontSize: '13px',
        color: '#ffaaff',
        backgroundColor: '#000000aa',
        padding: { x: 6, y: 3 },
      })
      .setOrigin(0.5, 0)
      .setDepth(6000);
    this.input.on('pointerdown', this.handleProjectileClick, this);
  }

  private uninstallProjectileInput(): void {
    this.input.off('pointerdown', this.handleProjectileClick, this);
    this.projectileFromMarker?.destroy();
    this.projectileToMarker?.destroy();
    this.projectilePathLine?.destroy();
    this.projectileHintText?.destroy();
    this.projectileFromMarker = null;
    this.projectileToMarker = null;
    this.projectilePathLine = null;
    this.projectileHintText = null;
    this.projectileFrom = null;
    this.projectileTo = null;
  }

  private handleProjectileClick(pointer: Phaser.Input.Pointer): void {
    const x = pointer.x;
    const y = pointer.y;
    if (!this.projectileFrom) {
      this.projectileFrom = { x, y };
      this.projectileFromMarker?.destroy();
      this.projectileFromMarker = this.add.circle(x, y, 6, 0x66ff66).setDepth(6001);
      if (this.projectileHintText) {
        this.projectileHintText.setText('Click 2 = destino');
      }
    } else if (!this.projectileTo) {
      this.projectileTo = { x, y };
      this.projectileToMarker?.destroy();
      this.projectileToMarker = this.add.circle(x, y, 6, 0xff6666).setDepth(6001);
      // Línea entre los dos puntos
      this.projectilePathLine?.destroy();
      this.projectilePathLine = this.add
        .graphics()
        .lineStyle(1, 0xaaaaaa, 0.4)
        .lineBetween(this.projectileFrom.x, this.projectileFrom.y, x, y)
        .setDepth(6000);
      this.projectileStartedAt = this.time.now;
      if (this.projectileHintText) {
        this.projectileHintText.setText('Loop activo. Click otra vez para reiniciar.');
      }
    } else {
      // Reiniciar
      this.projectileFromMarker?.destroy();
      this.projectileToMarker?.destroy();
      this.projectilePathLine?.destroy();
      this.projectileFromMarker = this.add.circle(x, y, 6, 0x66ff66).setDepth(6001);
      this.projectileFrom = { x, y };
      this.projectileTo = null;
      this.projectilePathLine = null;
      if (this.projectileHintText) {
        this.projectileHintText.setText('Click 2 = destino');
      }
    }
  }

  /** Calcula posición actual del projectile en loop (rebobina al llegar). */
  private getProjectilePosition(): { x: number; y: number } | null {
    if (!this.projectileFrom || !this.projectileTo) return null;
    const elapsed = (this.time.now - this.projectileStartedAt) % this.projectileDurationMs;
    const t = elapsed / this.projectileDurationMs;
    return {
      x: this.projectileFrom.x + (this.projectileTo.x - this.projectileFrom.x) * t,
      y: this.projectileFrom.y + (this.projectileTo.y - this.projectileFrom.y) * t,
    };
  }

  // ─── Anchor mode + métricas ────────────────────────────────────────────────

  /** Cambia el target del preview en vivo. */
  setAnchorMode(mode: EditorAnchorMode): void {
    const wasProjectile = this.anchorMode === 'projectile';
    this.anchorMode = mode;
    this.refreshAnchorDummies();
    if (mode === 'projectile') {
      this.installProjectileInput();
    } else if (wasProjectile) {
      this.uninstallProjectileInput();
    }
  }

  getAnchorMode(): EditorAnchorMode {
    return this.anchorMode;
  }

  getMetrics(): EditorMetrics {
    // El emitter expone numParticles internas; usamos el pool size declarado.
    const active = this.currentDef.numParticles;
    return {
      activeParticles: this.emitter && this.emitter.isRunning() ? active : 0,
      fps: this.game.loop.actualFps,
    };
  }

  // ─── Resolución de posición según anchor mode ─────────────────────────────

  private resolveAnchorPosition(cx: number, cy: number): { x: number; y: number } {
    switch (this.anchorMode) {
      case 'body_feet':
        return { x: cx, y: cy + BODY_OFFSET_Y + BODY_FRAME_HEIGHT / 2 };
      case 'body_center':
        return { x: cx, y: cy };
      case 'body_head':
        return {
          x: cx,
          y: cy + BODY_OFFSET_Y + BODY_FRAME_HEIGHT / 2 - HEAD_OFFSET_FROM_BODY_BOTTOM - HEAD_FRAME_HEIGHT / 2,
        };
      case 'helm_top':
        return {
          x: cx,
          y: cy + BODY_OFFSET_Y + BODY_FRAME_HEIGHT / 2 - HEAD_OFFSET_FROM_BODY_BOTTOM - HEAD_FRAME_HEIGHT,
        };
      case 'weapon_tip':
        return { x: cx + 24, y: cy - 16 };
      case 'weapon_hilt':
        return { x: cx + 18, y: cy };
      case 'shield_front':
        return { x: cx - 20, y: cy };
      case 'tile':
        return { x: cx, y: cy + 80 };
      case 'item_ground':
        return { x: cx, y: cy + 60 };
      case 'projectile': {
        const pos = this.getProjectilePosition();
        return pos ?? { x: cx, y: cy };
      }
    }
  }

  /** Muestra/oculta dummies según el anchor mode actual. */
  private refreshAnchorDummies(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;
    const showWeapon = this.anchorMode === 'weapon_tip' || this.anchorMode === 'weapon_hilt';
    const showShield = this.anchorMode === 'shield_front';
    const showTile = this.anchorMode === 'tile';
    const showItem = this.anchorMode === 'item_ground';

    if (showWeapon && !this.weaponSprite) {
      // Dummy: rectángulo plomizo simulando un arma vertical a la derecha del body
      this.weaponSprite = this.add.rectangle(cx + 18, cy - 8, 4, 36, 0x888888) as unknown as Phaser.GameObjects.Sprite;
      this.weaponSprite.setDepth(5002);
    }
    if (showShield && !this.shieldSprite) {
      this.shieldSprite = this.add.rectangle(cx - 18, cy, 14, 22, 0x665522) as unknown as Phaser.GameObjects.Sprite;
      this.shieldSprite.setDepth(5002);
    }
    if (showTile && !this.tileSprite) {
      this.tileSprite = this.add.rectangle(cx, cy + 80, 32, 32, 0x333344).setStrokeStyle(1, 0x555566);
      this.tileSprite.setDepth(4900);
    }
    if (showItem && !this.itemGroundSprite) {
      this.itemGroundSprite = this.add.rectangle(cx, cy + 60, 16, 16, 0x884422).setStrokeStyle(1, 0xaa6633);
      this.itemGroundSprite.setDepth(4950);
    }

    // Visibilidad
    if (this.weaponSprite) this.weaponSprite.setVisible(showWeapon);
    if (this.shieldSprite) this.shieldSprite.setVisible(showShield);
    if (this.tileSprite) this.tileSprite.setVisible(showTile);
    if (this.itemGroundSprite) this.itemGroundSprite.setVisible(showItem);
  }

  // ─── API pública ──────────────────────────────────────────────────────────

  applyDef(partial: Partial<ParticleEffectDef>): void {
    Object.assign(this.currentDef, partial);
    this.rebuildEmitter();
    ParticleEditorScene.onDefChanged?.(this.currentDef);
  }

  getDef(): ParticleEffectDef {
    return { ...this.currentDef };
  }

  importDef(def: ParticleEffectDef): void {
    this.currentDef = { ...def };
    if (!this.currentDef.spriteKey || !this.textures.exists(this.currentDef.spriteKey)) {
      this.currentDef.spriteKey = PARTICLE_SHEET.key;
    }
    this.rebuildEmitter();
    ParticleEditorScene.onDefChanged?.(this.currentDef);
  }

  playEffect(): void {
    this.rebuildEmitter();
  }

  stopEffect(): void {
    if (this.emitter) {
      this.emitter.stop();
    }
  }

  // ─── Internals ────────────────────────────────────────────────────────────

  private setupBodySpritesheet(): void {
    const key = 'editor_body1';
    const texture = this.textures.get(key);
    if (!texture || texture.key === '__MISSING') return;

    const source = texture.getSourceImage() as HTMLImageElement;

    // Cortar con bordes redondeados (sin drift si el PNG no es múltiplo exacto).
    addGridSpriteSheet(this, key, source, BODY_COLUMNS, BODY_ROWS);
  }

  private rebuildEmitter(): void {
    if (this.emitter) {
      this.emitter.destroy();
      this.emitter = null;
    }

    const key = this.currentDef.spriteKey;
    if (!key || !this.textures.exists(key)) return;

    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    this.emitter = new ParticleEmitter(this, this.currentDef, cx, cy);
    // body=5000, head=5001.
    // - depthMode='orbit': baseDepth=5000 (body); cada partícula se coloca
    //   en 5001 (sobre body) o 4999 (debajo) según renderY vs emitterY.
    // - behindEntity=true: 4999 (atrás).
    // - default: 5500 (delante).
    if (this.currentDef.depthMode === 'orbit') {
      this.emitter.setDepth(5000);
      this.emitter.setBaseDepth(5000);
    } else {
      const depth = this.currentDef.behindEntity ? 4999 : 5500;
      this.emitter.setDepth(depth);
    }
    this.emitter.start();
  }
}
